/**
 * 63.13 step 2, the gunship in play (DECISIONS 271; the sim is core/gunship.ts): a gunship flight drawn as the sheet's gunship
 * built R2 — the deck's swarm launchers, two heavy guided rockets a side on the sponsons' rails, its side gun at the match's
 * calibre (aircraftConcepts SIDE_GUN_CALIBRES) — banked as its pose has it (the host turns the ship; this the parts on it):
 * its side gun and its chin turret laid on what they last fired at (eased), their barrels recoiling, the chin's spinning
 * while it fires; the heavy rockets gone from their rails as its load spends them. And its guided rockets in flight (bullets
 * with a `guide`), each a rocket along its velocity with its exhaust lit: two instanced draws a kind. Presentation only: it
 * reads the flight and the events, never the reverse.
 */
import * as THREE from 'three';
import type { AutocannonCalibre, DropshipFlight, GunshipWeapon, Vec3, Bullet } from '../types';
import { buildAircraft, bakeAircraft, GUNSHIP_SWARM_LAUNCHER, GUNSHIP_HEAVY_ROCKET, type AircraftModel, type SideGunCalibre } from './aircraftConcepts';
import type { UnitPalette } from './unitPalette';
import type { DropshipModel } from './dropshipLooks';
import { copyModel, markTreeShared } from './modelCopy';

/** The look's dials: the guns' slew (radians a second), the barrels' recoil (a share of a person, its return seconds), the chin's spin while it fires. */
export const GUNSHIP_LOOK = { slew: 1.6, recoil: 0.35, recoilBack: 0.25, chinSpin: 40, chinSpinFor: 0.4 } as const;

/** A gunship's parts as drawn in play, and what they are doing. */
export interface GunshipView {
  a: AircraftModel;
  side: { traverse: THREE.Group; cradle: THREE.Group; barrel: THREE.Group; muzzle: THREE.Object3D; yaw0: number; yaw: number; down: number; want?: { yaw: number; down: number }; recoil: number } | null;
  chin: { turret: THREE.Group; cradle: THREE.Group | null; barrels: THREE.Group | null; yaw0: number; yaw: number; down: number; want?: { yaw: number; down: number }; spin: number } | null;
  /** 69.58: the roof's point-defence turret (GameConfig.shipTurrets), laid on the missile it fires at, its barrels spinning while it fires, its radar turning */
  roof: { turret: THREE.Group; cradle: THREE.Group; barrels: THREE.Group; muzzle: THREE.Object3D; radar: THREE.Group; yaw0: number; yaw: number; down: number; want?: { yaw: number; down: number }; spin: number } | null;
}

/** The gunship's model for a flight: built R2 with its side gun at `calibre`, baked; its ship (the dropship's model the host poses) and its view. */
export function buildGunshipView(palette: UnitPalette, person: number, calibre: SideGunCalibre, chin: AutocannonCalibre = 30, turret = false): { m: DropshipModel; view: GunshipView } {
  const a = buildAircraft('gunship', palette, person, { extra: 'gun105', r2: true, calibre, chin, turret });
  const pd = a.turret?.pd;
  bakeAircraft(a, true);   // (69.98: a gunship's clamps never open — folded with its hull)
  const g = a.extra?.gun, chinAt = a.mounts.find(q => q.gun === 'cannon30' || q.gun === 'cannon20')?.at as THREE.Group | undefined;
  const chinCradle = chinAt ? a.pitch.find(q => q.parent === chinAt) ?? null : null;
  const chinBarrels = chinCradle ? a.spin.find(s => s.group.parent === chinCradle)?.group ?? null : null;
  a.spin = a.spin.filter(s => s.group !== chinBarrels);   // (the chin's barrels spin as it fires, not with the engines)
  return {
    m: a.ship!,
    view: {
      a,
      side: g ? { ...g, yaw0: g.traverse.rotation.y, yaw: 0, down: g.cradle.rotation.x, recoil: 0 } : null,
      chin: chinAt ? { turret: chinAt, cradle: chinCradle, barrels: chinBarrels, yaw0: chinAt.rotation.y, yaw: 0, down: 0.3, spin: 0 } : null,
      roof: pd ? { ...pd, yaw0: pd.turret.rotation.y, yaw: 0, down: 0, spin: 0 } : null,
    },
  };
}

const bakedGunships = new Map<string, { root: THREE.Object3D; m: DropshipModel; view: GunshipView }>();
/**
 * 384: a copy of the one built and baked gunship for this palette and these guns (modelCopy.ts) — a flight's view. Geometry
 * and materials shared but each glow's own (its opacity follows the ship's thrust). A build was 38–40 ms in one frame in the
 * page (mega's one gunship flight in 75 s); the warm-up (ActionHost.warmParts) makes the template at load, so a flight's is
 * a copy. Dispose a copy with disposeObjectTree.
 */
export function bakedGunshipView(palette: UnitPalette, person: number, calibre: SideGunCalibre, chin: AutocannonCalibre = 30, turret = false): { m: DropshipModel; view: GunshipView } {
  const key = `${JSON.stringify(palette)}|${person}|${calibre}|${chin}|${turret}`;
  let t = bakedGunships.get(key);
  if (!t) {
    const b = buildGunshipView(palette, person, calibre, chin, turret);
    b.m.clamps = b.m.clamps.filter(c => c.bar.parent && c.hook.parent);   // (69.98: its clamps folded into the hull at the bake — their bars and hooks out of the tree, nothing left to move)
    t = { root: b.view.a.root, ...b };
    markTreeShared(t.root);
    bakedGunships.set(key, t);
  }
  const c = copyModel(t);
  const glows = new Set([...c.view.a.glows.map(g => g.mesh), ...c.m.glows.map(g => g.mesh)]);
  for (const mesh of glows) { const own = (mesh.material as THREE.Material).clone(); own.userData.shared = false; mesh.material = own; }   // (it goes with the copy: clone() carried the mark)
  return { m: c.m, view: c.view };
}

const _p = new THREE.Vector3(), _q = new THREE.Vector3();
/** The yaw (about the group's parent's y, from its rest) and depression that lay `g`'s +z on world point `to`. */
function layOn(g: THREE.Object3D, to: Vec3): { yaw: number; down: number } {
  const parent = g.parent!;
  parent.updateWorldMatrix(true, false);
  _p.set(to.x, to.y, to.z); parent.worldToLocal(_p);
  _q.copy(_p).sub(g.position);
  return { yaw: Math.atan2(_q.x, _q.z), down: Math.atan2(-_q.y, Math.hypot(_q.x, _q.z)) };
}

/**
 * A gunship fired (the Match's `gunshipFire`): the gun that fired laid on its aim, recoiled (the chin spun); the world point
 * its flash goes at and the flash's size (people) — the drawn muzzle for the guns, the sim's for the rockets.
 */
export function gunshipFired(v: GunshipView, weapon: GunshipWeapon, from: Vec3, to: Vec3): { at: Vec3; size: number } {
  if (weapon === 'side' && v.side) {
    const s = v.side, aim = layOn(s.traverse, to);
    s.want = { yaw: aim.yaw - s.yaw0, down: aim.down };
    s.recoil = 1;
    const at = s.muzzle.getWorldPosition(new THREE.Vector3());
    return { at: { x: at.x, y: at.y, z: at.z }, size: 0.8 + 0.5 * Math.min(1, (v.a.extra!.gun!.calibre - 40) / 36) };   // (the 40 mm's to the 76's)
  }
  if (weapon === 'chin' && v.chin) {
    const c = v.chin, aim = layOn(c.turret, to);
    c.want = { yaw: aim.yaw - c.yaw0, down: aim.down };
    c.spin = GUNSHIP_LOOK.chinSpinFor;
    const at = (c.barrels ?? c.turret).getWorldPosition(new THREE.Vector3());
    return { at: { x: at.x, y: at.y, z: at.z }, size: 0.6 };
  }
  if (weapon === 'heavy') return { at: from, size: 1.1 };
  return { at: from, size: weapon === 'swarm' ? 0.45 : 0.8 };
}

/** 69.58: the roof turret fired at `to` (the Match's `shipIntercept`): laid on it, its barrels spun; its drawn muzzle (world), null with no turret drawn. */
export function turretFired(v: GunshipView, to: Vec3): Vec3 | null {
  const r = v.roof;
  if (!r) return null;
  const aim = layOn(r.turret, to);
  r.want = { yaw: aim.yaw - r.yaw0, down: aim.down };
  r.spin = GUNSHIP_LOOK.chinSpinFor;
  const at = r.muzzle.getWorldPosition(new THREE.Vector3());
  return { x: at.x, y: at.y, z: at.z };
}

const ease = (a: number, b: number, step: number) => a + Math.max(-step, Math.min(step, b - a));
/** A frame of a gunship's parts: its guns slewed toward their aims, their recoil and spin run out, its heavy rockets shown as its load still holds them. */
export function updateGunshipView(v: GunshipView, f: DropshipFlight, dt: number): void {
  const L = GUNSHIP_LOOK, step = L.slew * dt;
  if (v.side) {
    const s = v.side;
    if (s.want) { s.yaw = ease(s.yaw, Math.max(-0.9, Math.min(0.9, s.want.yaw)), step); s.down = ease(s.down, Math.max(0.1, Math.min(1.4, s.want.down)), step); }
    s.traverse.rotation.y = s.yaw0 + s.yaw;
    s.cradle.rotation.x = s.down;
    s.recoil = Math.max(0, s.recoil - dt / L.recoilBack);
    s.barrel.position.z = 0.2 - L.recoil * s.recoil * s.recoil;
  }
  if (v.chin) {
    const c = v.chin;
    if (c.want) { c.yaw = ease(c.yaw, Math.max(-2.1, Math.min(2.1, c.want.yaw)), step * 2); c.down = ease(c.down, Math.max(0, Math.min(1.5, c.want.down)), step * 2); }
    c.turret.rotation.y = c.yaw0 + c.yaw;
    if (c.cradle) c.cradle.rotation.x = c.down;
    if (c.barrels && c.spin > 0) c.barrels.rotation.z += L.chinSpin * dt;
    c.spin = Math.max(0, c.spin - dt);
  }
  if (v.roof) {
    const r = v.roof;   // (a full turn either way and up to straight up: a missile comes from anywhere)
    if (r.want) { r.yaw = ease(r.yaw, Math.atan2(Math.sin(r.want.yaw), Math.cos(r.want.yaw)), step * 3); r.down = ease(r.down, Math.max(-1.5, Math.min(0.4, r.want.down)), step * 3); }
    r.turret.rotation.y = r.yaw0 + r.yaw;
    r.cradle.rotation.x = r.down;
    if (r.spin > 0) r.barrels.rotation.z += L.chinSpin * dt;
    r.spin = Math.max(0, r.spin - dt);
    r.radar.rotation.y += 1.8 * dt;
  }
  const heavy = v.a.heavy ?? [], left = f.gunship?.left.heavy ?? heavy.length, fired = heavy.length - left;
  heavy.forEach((g, i) => { g.visible = i >= fired; });
}

/**
 * The guided rockets in flight: the swarm's small and the heavy ones (by their burst's class — the swarm's the 40 mm
 * grenade's, the heavy's the LAW's: constants/gunship.ts), each instanced along its velocity, its exhaust lit behind it.
 */
export class GuidedRockets {
  private readonly kinds: Array<{ body: THREE.InstancedMesh; flame: THREE.InstancedMesh; r: number; len: number; n: number }>;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly s = new THREE.Vector3();
  private readonly z = new THREE.Vector3(0, 0, 1);
  private readonly d = new THREE.Vector3();
  private readonly at = new THREE.Vector3();

  constructor(scene: THREE.Scene, cap = 96) {
    const make = (r: number, len: number) => {
      const tube = new THREE.CylinderGeometry(r, r, len, 8); tube.rotateX(Math.PI / 2);
      const nose = new THREE.ConeGeometry(r, r * 3, 8); nose.rotateX(Math.PI / 2); nose.translate(0, 0, len / 2 + r * 1.5);
      const geo = mergeTwo(tube, nose);
      const flameGeo = new THREE.ConeGeometry(r * 1.2, r * 9, 8, 1, true); flameGeo.rotateX(-Math.PI / 2); flameGeo.translate(0, 0, -len / 2 - r * 4.5);
      const body = new THREE.InstancedMesh(geo, new THREE.MeshLambertMaterial({ color: '#c9c6b8' }), cap);
      body.name = 'gunshipGuidedRockets';
      const flame = new THREE.InstancedMesh(flameGeo, new THREE.MeshBasicMaterial({ color: '#ffb35a', transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }), cap);
      flame.name = 'gunshipGuidedRocketFlames';
      const k = { body, flame, r, len, n: 0 };
      for (const mesh of [k.body, k.flame]) { mesh.count = 0; mesh.frustumCulled = false; scene.add(mesh); }
      return k;
    };
    // (people: the model's sizes; scaled by the person at draw)
    this.kinds = [make(GUNSHIP_SWARM_LAUNCHER.r * 0.82 / 1.8, GUNSHIP_SWARM_LAUNCHER.len * 0.85 / 1.8), make(GUNSHIP_HEAVY_ROCKET.r / 1.8, GUNSHIP_HEAVY_ROCKET.len / 1.8)];
  }

  /** Draw this frame's guided rockets among `bullets` (`person` world units a person). */
  draw(bullets: readonly Bullet[], person: number): void {
    for (const k of this.kinds) k.n = 0;
    for (const b of bullets) {
      if (!b.guide) continue;
      const k = this.kinds[b.explosion === 'law' ? 1 : 0];
      if (k.n >= k.body.instanceMatrix.count) continue;
      this.d.set(b.vel.x, b.vel.y, b.vel.z).normalize();
      this.q.setFromUnitVectors(this.z, this.d);
      this.s.setScalar(person);
      this.at.set(b.pos.x, b.pos.y, b.pos.z);
      this.m.compose(this.at, this.q, this.s);
      k.body.setMatrixAt(k.n, this.m);
      k.flame.setMatrixAt(k.n, this.m);
      k.n++;
    }
    for (const k of this.kinds) {
      k.body.count = k.flame.count = k.n;
      k.body.instanceMatrix.needsUpdate = k.flame.instanceMatrix.needsUpdate = true;
    }
  }

  dispose(scene: THREE.Scene): void {
    for (const k of this.kinds) for (const mesh of [k.body, k.flame]) { scene.remove(mesh); mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); }
  }
}

/** Two non-indexed-compatible geometries as one (positions and normals; both made indexed by three's primitives). */
function mergeTwo(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
  const parts = [a.toNonIndexed(), b.toNonIndexed()];
  const pos: number[] = [], nor: number[] = [];
  for (const g of parts) { pos.push(...(g.getAttribute('position').array as Float32Array)); nor.push(...(g.getAttribute('normal').array as Float32Array)); }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  return out;
}
